import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { usePrimaryPropertyId } from "../hooks/useMyProperties";
import { Loader, SectionHeading, EmptyState } from "../components/Loader";
import { StatusPill, warrantyState } from "../components/StatusPill";
import { fmtDate, relativeDays } from "../lib/format";
import { ShieldCheck } from "lucide-react";

function WarrantyCard({ w }) {
  const state = warrantyState(w.expiration_date);
  return (
    <div className="bg-arq-card border border-border rounded-md p-5" data-testid={`warranty-${w.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-arq-accent font-semibold">{w.type}</p>
          <p className="font-medium text-arq-ink mt-0.5 leading-snug">{w.name}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{w.provider}</p>
        </div>
        <StatusPill status={state} />
      </div>
      {w.coverage && <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{w.coverage}</p>}
      <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-border">
        <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Start</p><p className="text-sm text-arq-ink">{fmtDate(w.start_date)}</p></div>
        <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Expires</p><p className="text-sm text-arq-ink">{fmtDate(w.expiration_date)}</p></div>
        <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Remaining</p><p className="text-sm text-arq-ink">{state === "Expired" ? "—" : relativeDays(w.expiration_date).replace("in ", "")}</p></div>
      </div>
    </div>
  );
}

export default function Warranty() {
  const { propertyId, isLoading } = usePrimaryPropertyId();
  const { data: warranties = [], isLoading: wl } = useQuery({
    queryKey: ["warranties", propertyId], queryFn: async () => (await api.get(`/properties/${propertyId}/warranties`)).data, enabled: !!propertyId });

  const groups = useMemo(() => {
    const g = { Builder: [], Structural: [], Manufacturer: [] };
    warranties.forEach((w) => { (g[w.type] || (g[w.type] = [])).push(w); });
    return g;
  }, [warranties]);

  if (isLoading || wl) return <Loader label="Loading warranties" />;

  return (
    <div className="space-y-8 arq-fade-up">
      <SectionHeading overline="Protect" title="Warranty Center" />
      {warranties.length === 0 && <EmptyState icon={ShieldCheck} title="No warranties on file yet" />}
      {["Builder", "Structural", "Manufacturer"].map((type) => (groups[type] && groups[type].length > 0) && (
        <section key={type}>
          <h2 className="font-display text-xl text-arq-ink mb-4">{type} Warrant{groups[type].length === 1 ? "y" : "ies"}</h2>
          <div className="grid sm:grid-cols-2 gap-4">{groups[type].map((w) => <WarrantyCard key={w.id} w={w} />)}</div>
        </section>
      ))}
    </div>
  );
}
